import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { CircularProgress, Stack, ThemeProvider } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { STAGES } from "@src/app/onboarding/model";
// eslint-disable-next-line no-restricted-imports
import { OnboardingRouterPath } from "@src/app/routing/constant/onboardingRoute";
import { CLIPBOARD_ORIENTATION_RESCHEDULED_BASE_PATH } from "@src/appV2/ClipboardOrientation/paths";
import { type OrientationRescheduledLocationState } from "@src/appV2/ClipboardOrientation/types";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { ACCOUNTS_BASE_PATH } from "@src/appV2/redesign/Accounts/paths";
import { SHIFT_DISCOVERY_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { RootPaths } from "../../App/paths";
import { PullToRefresh } from "../../lib/PullToRefresh/PullToRefresh";
import { IconButton } from "../../redesign/components/IconButton";
import { PageWrapper } from "../../redesign/components/PageWrapper";
import {
  type AssessmentQualification,
  useSkillsAssessments,
} from "../../SkillsAssessment/api/useSkillsAssessments";
import { type AssessmentEvent, type SkillsAssessmentStatus } from "../../SkillsAssessment/types";
import { useDefinedWorker } from "../../Worker/useDefinedWorker";
import { LIMIT_SHIFTS_ACCESS_BASED_ON_TIMELINESS_EXPERIMENT_TYPE } from "../constants";
import { OrientationNotRequired } from "./OrientationNotRequired";
import { ClipboardOrientationStatus } from "./Status/ClipboardOrientationStatus";
import { OrientationFlow } from "./VideoFlow/OrientationFlow";
import { TimelinessRewardScreen } from "./VideoFlow/TimelinessRewardScreen";

interface Props {
  isSignup?: boolean;
}

function CommonPageWrapper(props: { children: React.ReactNode; onBack?: () => void }) {
  const { children, onBack } = props;
  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => ({
          ...getSafeAreaPaddings({
            top: true,
            bottom: true,
            fallbackBottom: theme.spacing(4),
          }),
          background: "#fff",
        })}
      >
        <Stack p={5} height="100%">
          {onBack && (
            <IconButton
              aria-label="Back"
              iconType="chevron-left"
              size="medium"
              variant="outlined"
              onClick={onBack}
            />
          )}
          <Stack flex={1} alignItems="center">
            {children}
          </Stack>
        </Stack>
      </PageWrapper>
    </ThemeProvider>
  );
}

export function ClipboardOrientationLandingPage(props: Props) {
  const { isSignup = false } = props;

  const history = useHistory<OrientationRescheduledLocationState>();
  const location = useLocation();
  const { showSuccessToast } = useToast();

  const worker = useDefinedWorker();

  const {
    data: assessments,
    status,
    isRefetching,
    refetch,
  } = useSkillsAssessments({
    workerId: worker.userId,
  });

  const orientation = assessments?.data?.[0];

  const latestScheduledEventId =
    assessments?.data?.[0]?.relationships.latestScheduledEvent.data?.id;
  const assessmentStatusId = assessments?.data?.[0]?.relationships.assessmentStatus.data?.id;
  const assessmentQualificationId = assessments?.data?.[0]?.relationships.qualification.data?.id;

  const assessmentQualification = assessments?.included.find(
    (included) => included.type === "qualification" && included.id === assessmentQualificationId
  ) as AssessmentQualification | undefined;
  const assessmentStatus = assessments?.included.find(
    (included) => included.type === "assessment-status" && included.id === assessmentStatusId
  ) as SkillsAssessmentStatus | undefined;
  const latestScheduledEvent = assessments?.included.find(
    (included) => included.type === "assessment-event" && included.id === latestScheduledEventId
  ) as AssessmentEvent | undefined;

  const isLimitedAccessExperimentEnabled =
    orientation?.attributes?.experimentDetails?.experimentType ===
    LIMIT_SHIFTS_ACCESS_BASED_ON_TIMELINESS_EXPERIMENT_TYPE;

  const onSchedule = () => {
    void refetch();
    if (isSignup) {
      history.push(OnboardingRouterPath.ONBOARDING_INFO1);
    }
  };

  const onPassOrFail = () => {
    if (isSignup) {
      history.push(STAGES.scheduleAssessmentConfirmation.nextScreen);
      return;
    }

    history.push(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_PATH}`);
  };

  const onReschedule = () => {
    void refetch();
    history.push(CLIPBOARD_ORIENTATION_RESCHEDULED_BASE_PATH, {
      origin: "CLIPBOARD_ORIENTATION_LANDING_PAGE",
    });
  };

  const onBack = () => {
    if (isDefined(location.key)) {
      history.goBack();
    } else if (isSignup) {
      history.push(OnboardingRouterPath.ONBOARDING_INFO1);
    } else {
      history.push(ACCOUNTS_BASE_PATH);
    }
  };

  useLogEffect(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_LANDING_PAGE_VIEWED);

  // Most of the UI is driven by Orientation current status from backend
  // but we need to show the reward screen only once after the orientation is completed
  // So we need to have a state for this
  const [showDemoShiftRewardScreen, setShowDemoShiftRewardScreen] = useState(false);
  const onComplete = useCallback(async () => {
    if (isLimitedAccessExperimentEnabled) {
      setShowDemoShiftRewardScreen(true);
    }

    await refetch();
  }, [isLimitedAccessExperimentEnabled, refetch]);

  const onContinueForTimelinessRewardScreen = useCallback(() => {
    setShowDemoShiftRewardScreen(false);
  }, []);

  if (status === "loading") {
    return (
      <CommonPageWrapper onBack={onBack}>
        <CircularProgress />
      </CommonPageWrapper>
    );
  }

  if (
    status === "success" &&
    orientation &&
    assessmentStatus?.attributes.isAllowedToStartAssessment
  ) {
    return (
      <ThemeProvider theme={getTheme()}>
        <OrientationFlow
          orientation={orientation}
          orientationStatus={assessmentStatus}
          workerId={worker.userId}
          orientationVideos={orientation.attributes.orientationVideos ?? []}
          qualificationName={assessmentQualification?.attributes.name ?? ""}
          // TODO: Send this from the backend
          timeToCompleteInMinutes={10}
          isRefreshingOrientation={isRefetching}
          onComplete={onComplete}
        />
      </ThemeProvider>
    );
  }

  if (
    showDemoShiftRewardScreen &&
    isLimitedAccessExperimentEnabled &&
    assessmentStatus &&
    orientation
  ) {
    return (
      <CommonPageWrapper>
        {isRefetching ? (
          <CircularProgress />
        ) : (
          <TimelinessRewardScreen
            orientation={orientation}
            orientationStatus={assessmentStatus}
            onContinue={onContinueForTimelinessRewardScreen}
          />
        )}
      </CommonPageWrapper>
    );
  }

  return (
    <CommonPageWrapper onBack={onBack}>
      {status === "success" && !orientation && <OrientationNotRequired />}
      {status === "success" &&
        orientation &&
        !assessmentStatus?.attributes.isAllowedToStartAssessment && (
          <>
            <PullToRefresh onRefresh={refetch} />
            <ClipboardOrientationStatus
              isRefetchingData={isRefetching}
              assessment={orientation}
              assessmentStatus={assessmentStatus}
              scheduledFor={latestScheduledEvent?.attributes.scheduledFor}
              qualificationName={assessmentQualification?.attributes.name ?? ""}
              onSchedule={() => {
                showSuccessToast("Demo Shift successfully scheduled!");
                onSchedule();
              }}
              onReschedule={() => {
                showSuccessToast("Demo Shift successfully rescheduled!");
                onReschedule();
              }}
              onComplete={onPassOrFail}
              onBackAfterFail={onPassOrFail}
            />
          </>
        )}
    </CommonPageWrapper>
  );
}
